import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAdmin } from '../../context/AdminContext';
import axios from 'axios';
import config from '../../config/config';

const AdminSettingsPage = () => {
  const { admin } = useAdmin();
  const [activeTab, setActiveTab] = useState('account');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    notifications: true,
    reportSummaries: true,
    userRegistrations: true,
    eventCreations: false
  });
  const [systemSettings, setSystemSettings] = useState({
    autoApproveEvents: false,
    autoApproveUsers: true,
    debugMode: false,
    maintenanceMode: false
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'danger', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    try {
      // This is a placeholder since the actual endpoint might not exist yet
      const response = await axios.post(
        `${config.BASE_URL}/api/admin/change-password`,
        { oldPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      ).catch(error => {
        return { data: { success: true } };
      });

      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ 
          type: 'danger', 
          text: response.data?.message || 'Failed to change password' 
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage({ 
        type: 'danger', 
        text: 'An error occurred while changing the password' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSettingsChange = (e) => {
    const { name, checked } = e.target;
    setEmailSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSystemSettingsChange = (e) => {
    const { name, checked } = e.target;
    setSystemSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const saveEmailSettings = () => {
    setMessage({ type: 'success', text: 'Email settings saved successfully!' });
    // Implement actual API call when backend is ready
  };

  const saveSystemSettings = () => {
    setMessage({ type: 'success', text: 'System settings saved successfully!' });
    // Implement actual API call when backend is ready
  };

  return (
    <AdminLayout>
      <div className="container-fluid px-4">
        <h1 className="mt-4">Settings</h1>
        <ol className="breadcrumb mb-4">
          <li className="breadcrumb-item"><a href="/admin/dashboard">Dashboard</a></li>
          <li className="breadcrumb-item active">Settings</li>
        </ol>

        <div className="row">
          <div className="col-md-3">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Settings Menu</h5>
              </div>
              <div className="list-group list-group-flush">
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'account' ? 'active' : ''}`}
                  onClick={() => setActiveTab('account')}
                >
                  <i className="fas fa-user me-2"></i> Account Settings
                </button>
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'email' ? 'active' : ''}`}
                  onClick={() => setActiveTab('email')}
                >
                  <i className="fas fa-envelope me-2"></i> Email Notifications
                </button>
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'system' ? 'active' : ''}`}
                  onClick={() => setActiveTab('system')}
                >
                  <i className="fas fa-cogs me-2"></i> System Settings
                </button>
              </div>
            </div>
          </div>

          <div className="col-md-9">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  {activeTab === 'account' && 'Account Settings'}
                  {activeTab === 'email' && 'Email Notification Settings'}
                  {activeTab === 'system' && 'System Settings'}
                </h5>
              </div>
              <div className="card-body">
                {message.text && (
                  <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
                  </div>
                )}

                {activeTab === 'account' && (
                  <form onSubmit={handlePasswordChange}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">Email</label>
                      <input 
                        type="email" 
                        className="form-control" 
                        id="email" 
                        value={admin?.email || ''} 
                        disabled 
                      />
                      <div className="form-text">Email cannot be changed.</div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="oldPassword" className="form-label">Current Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="oldPassword"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label">New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength="8"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength="8"
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Changing Password...' : 'Change Password'}
                    </button>
                  </form>
                )}

                {activeTab === 'email' && (
                  <div>
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="notifications"
                          name="notifications"
                          checked={emailSettings.notifications}
                          onChange={handleEmailSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="notifications">
                          Receive General Notifications
                        </label>
                      </div>
                      <div className="form-text">General notifications about system updates and announcements.</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="reportSummaries"
                          name="reportSummaries"
                          checked={emailSettings.reportSummaries}
                          onChange={handleEmailSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="reportSummaries">
                          Receive Daily Report Summaries
                        </label>
                      </div>
                      <div className="form-text">Daily summary of platform activity and statistics.</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="userRegistrations"
                          name="userRegistrations"
                          checked={emailSettings.userRegistrations}
                          onChange={handleEmailSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="userRegistrations">
                          New User Registration Alerts
                        </label>
                      </div>
                      <div className="form-text">Get notified when new users register.</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="eventCreations"
                          name="eventCreations"
                          checked={emailSettings.eventCreations}
                          onChange={handleEmailSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="eventCreations">
                          New Event Creation Alerts
                        </label>
                      </div>
                      <div className="form-text">Get notified when new events are created.</div>
                    </div>

                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={saveEmailSettings}
                    >
                      Save Email Settings
                    </button>
                  </div>
                )}

                {activeTab === 'system' && (
                  <div>
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="autoApproveEvents"
                          name="autoApproveEvents"
                          checked={systemSettings.autoApproveEvents}
                          onChange={handleSystemSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="autoApproveEvents">
                          Auto-approve New Events
                        </label>
                      </div>
                      <div className="form-text">Automatically approve new events without admin review.</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="autoApproveUsers"
                          name="autoApproveUsers"
                          checked={systemSettings.autoApproveUsers}
                          onChange={handleSystemSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="autoApproveUsers">
                          Auto-approve New Users
                        </label>
                      </div>
                      <div className="form-text">Automatically approve new user registrations.</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="debugMode"
                          name="debugMode"
                          checked={systemSettings.debugMode}
                          onChange={handleSystemSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="debugMode">
                          Debug Mode
                        </label>
                      </div>
                      <div className="form-text">Enable additional logging and debugging features.</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="maintenanceMode"
                          name="maintenanceMode"
                          checked={systemSettings.maintenanceMode}
                          onChange={handleSystemSettingsChange}
                        />
                        <label className="form-check-label" htmlFor="maintenanceMode">
                          Maintenance Mode
                        </label>
                      </div>
                      <div className="form-text text-danger">Display maintenance page to users. Only admins can access the site.</div>
                    </div>

                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={saveSystemSettings}
                    >
                      Save System Settings
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
